import { useQueryClient } from '@tanstack/react-query';
import classNames from 'classnames';
import get from 'lodash/get';
import { BaseSyntheticEvent, useContext, useEffect } from 'react';
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import { useForm } from 'react-hook-form';
import { BsPencil } from 'react-icons/bs';
import { toast } from 'react-toastify';
import { bookingRequestsQueryKeys } from '../../../query-keys-factories/booking-requests';
import { handleApiError } from '../../../utils/errorhandling';
import { CustomizedInput } from '../../CustomizedInput';
import { InfoIcon } from '../../InfoIcon';
import { TooltippedText } from '../../TooltippedText';
import { getMinutesFromTime, prepareRequestBody } from '../BookingRequestDialog.utils';
import { ExtraIncomeContext } from '../ExtraIncomeProvider';
import { extraIncomeTexts } from './ExtraIncomeForms.const';
import { ExtraIncomeFieldType, ExtraIncomeFormType } from './ExtraIncomeForms.types';
import './ExtraIncomeForms.scss';

export const ExtraIncomeForms = ({
  refetch,
  className,
}: {
  refetch: () => void;
  className?: string;
}) => {
  const { extraIncome, isLoading, saveBookingRequestExtra, requestId } =
    useContext(ExtraIncomeContext);
  const queryClient = useQueryClient();
  const {
    register,
    formState: { errors: formErrors },
    handleSubmit,
    reset,
    setValue,
    watch,
    setError,
    getValues,
    clearErrors,
  } = useForm<ExtraIncomeFormType>({
    defaultValues: {},
  });

  const { extraHours, additionalCharges, saveButton } = extraIncomeTexts;

  const onSubmit = async (data: ExtraIncomeFormType, event?: BaseSyntheticEvent) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (data.extraIncome) {
      const { spentMinutes, totalAmount } = data.extraIncome;
      const totalMinutes = getMinutesFromTime(spentMinutes);
      if (Number(totalAmount) && !totalMinutes) {
        setError('extraIncome.spentMinutes', {
          type: 'manual',
          message: extraHours.fields.timeSpent.errorMessage,
        });
        return;
      }
      if (!Number(totalAmount) && totalMinutes) {
        setError('extraIncome.totalAmount', {
          type: 'manual',
          message: extraHours.fields.totalAmount.errorMessage,
        });
        return;
      }

      const requestBody = prepareRequestBody(data.extraIncome, extraIncome);

      try {
        await saveBookingRequestExtra?.({
          requestId,
          requestBody,
        });
        await queryClient.invalidateQueries(
          bookingRequestsQueryKeys.history({ requestId, limit: 50, offset: 0 })
        );
        refetch();
        reset({
          ...getValues(),
        });
        toast.success(extraIncomeTexts.successMessage);
      } catch (e) {
        handleApiError(e);
      }
    }
  };

  const extraIncomeValues = watch('extraIncome', extraIncome);

  useEffect(() => {
    clearErrors();
    setValue('extraIncome', extraIncome);
  }, [extraIncome, setValue, clearErrors]);

  const manualSubmit = (e: BaseSyntheticEvent) => {
    e?.preventDefault();
    const values = getValues();
    return onSubmit(values);
  };

  const onChange = (name: ExtraIncomeFieldType, e: { target; type? }, cb?: () => void) => {
    setValue(name, e.target.value);
    if (get(formErrors, `${name}.message`)) {
      clearErrors();
    }
    cb?.();
  };

  return (
    <Form
      noValidate
      onSubmit={handleSubmit(onSubmit)}
      className={classNames('extra-income-forms', className)}
    >
      <Row className="align-items-stretch flex-lg-wrap h-100">
        <Col className="pb-3 col-12 d-flex flex-column">
          <div className="p-3 border-top flex-grow-1">
            <div className="d-flex justify-content-start flex-grow-1 pb-2">
              <h5 className="text-section-name--dark-brand">{additionalCharges.title}</h5>
              <TooltippedText tooltipContent={additionalCharges.tooltip}>
                <InfoIcon size={14} className="tooltip-icon" />
              </TooltippedText>
            </div>
            <Row>
              <Col xs={12} sm={6} className="extra-income-forms-input">
                <CustomizedInput
                  type="number"
                  step="0.01"
                  title={additionalCharges.fields.bonus.label}
                  isEmpty={!extraIncomeValues.additionalBonus}
                  isLoading={isLoading}
                  suffixIcon={<BsPencil />}
                  value={extraIncomeValues.additionalBonus}
                  placeholder="0"
                  submitHandler={manualSubmit}
                  suffixFocusIcon={
                    <Button
                      variant="tertiary"
                      size="sm"
                      type="submit"
                      className="submit-extra-button"
                    >
                      {saveButton.label}
                    </Button>
                  }
                  suffixFocusedClassName="submit-extra-button-wrapper"
                  suffixEmptyIcon={<span>{additionalCharges.fields.bonus.suffix}</span>}
                  isInvalid={!!formErrors.extraIncome?.additionalBonus?.message}
                  register={register}
                  name="extraIncome.additionalBonus"
                  onChange={(e) => onChange('extraIncome.additionalBonus', e)}
                  titleClassName="text-muted"
                />
              </Col>
              <Col xs={12} sm={6} className="extra-income-forms-input">
                <CustomizedInput
                  type="number"
                  step="0.01"
                  title={additionalCharges.fields.travelExpenses.label}
                  isEmpty={!extraIncomeValues.travelExpenses}
                  value={extraIncomeValues.travelExpenses}
                  isLoading={isLoading}
                  placeholder="0.00"
                  suffixIcon={<BsPencil />}
                  submitHandler={manualSubmit}
                  suffixFocusIcon={
                    <Button
                      variant="tertiary"
                      size="sm"
                      type="submit"
                      className="submit-extra-button"
                    >
                      {saveButton.label}
                    </Button>
                  }
                  suffixFocusedClassName="submit-extra-button-wrapper"
                  suffixEmptyIcon={<span>{additionalCharges.fields.travelExpenses.suffix}</span>}
                  isInvalid={!!formErrors.extraIncome?.travelExpenses?.message}
                  register={register}
                  name="extraIncome.travelExpenses"
                  onChange={(e) => onChange('extraIncome.travelExpenses', e)}
                  titleClassName="text-muted"
                />
              </Col>
            </Row>
          </div>
        </Col>
      </Row>
    </Form>
  );
};
